import type { DraftState, PoolSuggestion, WizardItemType } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { PoolColumnDetection } from '../../../Util/sheetItemRoles/index.ts'
import { analyzeTypeColumn, judgePoolColumn } from '../../../Util/sheetItemRoles/index.ts'
import { hasKeyColumn } from './sheetMapping.ts'
import { sheetMatchOf } from './sheetMatching.ts'
import { placedGroups, poolsWithoutControl } from '../pools.ts'

/**
 * What the sample sheet's type and pool columns say about the draft's samples, read through the shared
 * detector. In an archive order the controls are sheet samples inside the archives, so neither applies.
 */

const archiveOrder = (draft: DraftState) => draft.settings?.sampleConfiguration.value === 'COMBINED_SAMPLE_PAIRS'

const matchedSheet = (draft: DraftState) => {
  const sheet = draft.sheet
  if (!sheet || !hasKeyColumn(sheet.mapping) || archiveOrder(draft)) {
    return undefined
  }
  const byGroup = sheetMatchOf(draft)?.byGroup ?? {}
  const groupOfRow = new Map(Object.entries(byGroup).map(([groupKey, row]) => [row, groupKey]))
  return { sheet, byGroup, groupOfRow }
}

/** The type the sheet states per matched group; a row stating none, or a value the detector cannot map, is left out. */
export const sheetTypesOf = (draft: DraftState): Record<string, WizardItemType> => {
  const matched = matchedSheet(draft)
  const column = matched?.sheet.mapping.itemType
  if (!matched || !column) {
    return {}
  }
  const roles = analyzeTypeColumn(column, matched.sheet.rows).roles
  return Object.fromEntries(
    Object.entries(matched.byGroup).flatMap(([groupKey, row]) => {
      const role = roles[row]
      return role ? [[groupKey, role] as const] : []
    })
  )
}

export type SheetPools = {
  detection: PoolColumnDetection
  /** Pool per matched group key, numbered 0, 1, … in the order the sheet first names each pool. */
  pools: Record<string, number>
  /** The pool names the sheet gives, by those numbers; none when its values cannot name pools. */
  names: Record<number, string>
  count: number
}

/**
 * The sheet's pool column judged against the draft's samples as they are now labelled, so a control the
 * lab marks by hand counts. Present whenever a pool column is mapped, for its evidence; `pools` is empty
 * when the column groups nothing.
 */
export const sheetPoolsOf = (draft: DraftState): SheetPools | undefined => {
  const matched = matchedSheet(draft)
  const column = matched?.sheet.mapping.pool
  if (!matched || !column) {
    return undefined
  }
  const { sheet, groupOfRow } = matched
  const typeRoles = sheet.mapping.itemType ? analyzeTypeColumn(sheet.mapping.itemType, sheet.rows).roles : []
  const detection = judgePoolColumn(column, sheet.rows, {
    isControl: (row) => {
      const groupKey = groupOfRow.get(row)
      return (groupKey ? draft.groups[groupKey]?.itemType : typeRoles[row]) === 'NEGATIVE_CONTROL'
    },
    itemKey: (row) => groupOfRow.get(row) ?? `row-${row}`
  })
  const pools: Record<string, number> = {}
  const names: Record<number, string> = {}
  let count = 0
  if (detection.usable) {
    for (const group of detection.groups) {
      const keys = group.rows.flatMap((row) => groupOfRow.get(row) ?? [])
      if (keys.length === 0) {
        continue
      }
      keys.forEach((key) => (pools[key] = count))
      if (detection.namesUsable && group.name) {
        names[count] = group.name
      }
      count++
    }
  }
  return { detection, pools, names, count }
}

/** The sheet's pools as the pools question offers them, with the reason they were not applied on their own. */
export const sheetSuggestion = (draft: DraftState, sheet: SheetPools): PoolSuggestion => ({
  pools: sheet.pools,
  count: sheet.count,
  method: 'sheet',
  withoutControl: poolsWithoutControl(draft, sheet.pools),
  reason: sheet.detection.reason
})

/**
 * The sheet's pools laid onto the draft while nothing the lab did says otherwise: unanswered, or answered
 * by the sheet itself. A manual split, "one pool" or an accepted file-name suggestion beat it. Only a
 * column that passes the control rule on its own is applied; otherwise the pools question offers it.
 */
export const applySheetPools = (draft: DraftState): DraftState => {
  if (draft.poolsAnswered && draft.poolsSource !== 'sheet') {
    return draft
  }
  const sheet = sheetPoolsOf(draft)
  if (!sheet?.detection.auto || sheet.count < 2) {
    return draft
  }
  let changed = !draft.poolsAnswered || draft.poolsSource !== 'sheet'
  const groups = Object.fromEntries(
    Object.entries(draft.groups).map(([key, group]) => {
      const pool = key in sheet.pools ? sheet.pools[key] || undefined : group.pool
      changed ||= pool !== group.pool
      return [key, pool === group.pool ? group : { ...group, pool }]
    })
  )
  return changed ? { ...draft, groups, poolsAnswered: true, poolsSource: 'sheet' } : draft
}

/**
 * A name per pool of `pools` where all of its samples that have a sheet pool share one: the sheet's name
 * for that pool. A pool the lab mixed, or one no sheet row reaches, keeps its number.
 */
export const poolNamesOf = (draft: DraftState, pools: Readonly<Record<string, number>>): Record<number, string> => {
  const sheet = sheetPoolsOf(draft)
  if (!sheet || Object.keys(sheet.names).length === 0) {
    return {}
  }
  const named = new Map<number, Set<string | undefined>>()
  for (const [key, pool] of Object.entries(pools)) {
    if (key in sheet.pools) {
      const names = named.get(pool) ?? new Set()
      names.add(sheet.names[sheet.pools[key]])
      named.set(pool, names)
    }
  }
  const result: Record<number, string> = {}
  const used = new Set<string>()
  for (const [pool, names] of [...named.entries()].sort(([a], [b]) => a - b)) {
    const [name] = [...names]
    if (names.size === 1 && name && !used.has(name)) {
      result[pool] = name
      used.add(name)
    }
  }
  return result
}

/** Whether two pool assignments group the same keys together, whatever their numbers. */
export const samePartition = (a: Readonly<Record<string, number>>, b: Readonly<Record<string, number>>): boolean => {
  const keys = Object.keys(a)
  if (keys.length !== Object.keys(b).length || keys.some((key) => !(key in b))) {
    return false
  }
  const forward = new Map<number, number>()
  const backward = new Map<number, number>()
  return keys.every((key) => {
    const [x, y] = [a[key], b[key]]
    if ((forward.has(x) && forward.get(x) !== y) || (backward.has(y) && backward.get(y) !== x)) {
      return false
    }
    forward.set(x, y)
    backward.set(y, x)
    return true
  })
}

export type PoolLabelDue = { poolId: string; label?: string }

/**
 * The server pools whose label may change: those the sheet names, and those whose name the wizard wrote
 * once and no longer matches.
 */
export const poolLabelsDue = (draft: DraftState): PoolLabelDue[] => {
  const pools = Object.fromEntries(placedGroups(draft).map((group) => [group.key, group.pool ?? 0] as const))
  const names = poolNamesOf(draft, pools)
  return draft.poolIds.flatMap((poolId, index) => {
    if (!poolId) {
      return []
    }
    const name = names[index]
    const written = draft.poolLabels?.[poolId]
    const due = name !== undefined ? name !== written : !!written
    return due ? [{ poolId, label: name }] : []
  })
}
